import { Component, ContentChild, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AbstractValueAccessorComponent, ValueAccessorUtil } from '@railmybox/shared/util';
import { TranslocoService } from '@jsverse/transloco';
import { NgxMaskOptions } from 'ngx-mask';
import { FieldPrefixDirective, FieldSuffixDirective } from '@railmybox/shared/directive';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'eg-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [...ValueAccessorUtil.getValueAccessorProvider(InputComponent)],
  encapsulation: ViewEncapsulation.Emulated,
  standalone: false,
})
export class InputComponent extends AbstractValueAccessorComponent<string | number> implements OnInit {
  private translocoService = inject(TranslocoService);

  @Input() type = 'text';
  @Input() autocomplete?: string;
  @Input() prefixInputPadding = 10;
  @Input() numberMask = 'separator.2';
  @Input() changedValue = false;
  inputSeparator: NgxMaskOptions['decimalMarker'];
  decimalMarker: NgxMaskOptions['decimalMarker'];
  typed = false;

  @Output() keyDown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

  @ContentChild(FieldPrefixDirective, { read: ElementRef }) set prefix(prefix: ElementRef) {
    if (prefix) {
      this.hasPrefixIcon = !!prefix.nativeElement;
      this.prefixContainerWidth = prefix.nativeElement.offsetWidth < 80 ? prefix.nativeElement.offsetWidth : 80;
    }
  }

  @ContentChild(FieldSuffixDirective, { read: ElementRef }) set suffix(suffix: ElementRef) {
    if (suffix) {
      const tag = suffix.nativeElement.tagName.toLowerCase();
      this.hasSuffixIcon = ['img', 'svg', 'i'].includes(tag);
    }
  }

  hasPrefixIcon = false;
  hasSuffixIcon = false;
  prefixContainerWidth?: number;

  ngOnInit(): void {
    this.translocoService.langChanges$.pipe(untilDestroyed(this)).subscribe((lang) => {
      this.inputSeparator = lang === 'de' ? '.' : ',';
      this.decimalMarker = lang === 'de' ? ',' : '.';
      this.changeDetectorRef.detectChanges();
    });
  }

  /**
   * Event handler when input is change.
   */
  inputChanged(): void {
    const numericValue = this.value === '' ? null : +this.value;

    if (this.type === 'number') {
      this.onChange(numericValue);
      this.formControl?.setValue(numericValue);
      this.changed.emit(numericValue);
    } else {
      this.onChange(this.value);
      this.formControl?.setValue(this.value);
      this.changed.emit(this.value);
    }
  }

  handleKeyDown(event: KeyboardEvent) {
    this.keyDown.emit(event);
  }

  handleKeyUp(event: KeyboardEvent) {
    const value = (event.target as HTMLInputElement).value;
    this.onChange(value);
    this.formControl?.setValue(value);
    this.changed.emit(value);
  }

  onNumberKeyup(event: KeyboardEvent) {
    this.onChange(this.value);
    this.formControl?.setValue(this.value);
    this.changed.emit(this.value);

    if (!this.isIgnoredKey(event.key)) {
      this.typed = true;
    }
  }

  isIgnoredKey(key: string) {
    return [
      'Tab',
      'Shift',
      'Control',
      'Alt',
      'Meta',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Escape',
      'Enter',
      'Space',
      'Backspace',
    ].includes(key);
  }

  resetTyped() {
    this.typed = false;
  }
}
